import { useQuery } from "@tanstack/react-query";
import { apiFetch, ApiError } from "../lib/api.js";
import type { MeResponse } from "../lib/types.js";
import { LOCAL_TZ } from "../lib/timezone.js";
import type { AsyncResult } from "./useApi.js";

/**
 * GET /me — cookie-based session+user identity. The unauthenticated case
 * is a 200 with `user: null`, NOT a 401, per the bridge contract.
 *
 * Backed by TanStack Query under key `['me']` so every consumer
 * (Nav, FieldNav, Dashboard, GameDetail, Placement, Results, Subscription,
 * SigninRoute) shares one in-flight request and one cache entry. Auth
 * state changes invalidate the cache from:
 *   - VerifyRoute (magic-link success)
 *   - PhoneForm (phone-OTP success)
 *   - AuthMenu (sign out)
 *
 * Returns the legacy `{ data, err, loading, reload }` shape so existing
 * call sites don't change.
 */
export function useMe(): AsyncResult<MeResponse> {
  const q = useQuery<MeResponse>({
    queryKey: ["me"],
    queryFn: () =>
      apiFetch<MeResponse>(LOCAL_TZ ? `/me?tz=${encodeURIComponent(LOCAL_TZ)}` : "/me"),
    // 60s stale — auth state should reflect sign-in/out within ~a minute
    // of focus, but doesn't need to refetch on every navigation.
    staleTime: 60_000,
    gcTime: 10 * 60_000,
  });
  return {
    data: q.data ?? null,
    err: q.error
      ? q.error instanceof ApiError
        ? `${q.error.status}: ${q.error.message}`
        : q.error.message
      : null,
    loading: q.isPending,
    reload: () => {
      void q.refetch();
    },
  };
}
